import { Loader } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { InstanceResponseModel } from '../../../../../apis/v1/instances/model';
import { Button } from '../../../../../components/Atoms';
import DialogBox from '../../../../../components/Atoms/DialogBox/DialogBox';
import { useResizeOptionsQuery } from '../hooks/useResizeOptionsQuery';
import ResizeInstanceModal from './ResizeInstanceModal';

interface ResizeInstanceCardProps {
    instance: InstanceResponseModel;
}

export default function ResizeInstanceCard({ instance }: ResizeInstanceCardProps) {
    const { t } = useTranslation('cloudComputeInstance');
    const [opened, { open, close }] = useDisclosure(false);
    const { data } = useResizeOptionsQuery(instance.id, true);

    const isResizing = instance.state.power === 'resizing';
    const atMaximum = !!data && data.packages.length === 0;

    return (
        <DialogBox label={t('resize.title')} classNames="p-4">
            <div className="w-full flex flex-row items-center justify-between gap-4">
                <div className="flex flex-col gap-1">
                    <span className="text-sm text-fg/68">{t('resize.description')}</span>
                    <span className="text-sm text-fg">
                        {t('resize.currentPlan', {
                            cpu: instance.spec.cpu,
                            memory: instance.spec.memory / 1024,
                            disk: instance.spec.disk,
                            hourly: instance.price.hourly.toFixed(2),
                        })}
                    </span>
                    {atMaximum && (
                        <span className="text-sm text-yellow-400/80 light:text-yellow-800">
                            {t('resize.maxReached')}
                        </span>
                    )}
                </div>
                <Button onClick={open} disabled={isResizing || atMaximum} size="lg">
                    {isResizing ? (
                        <div className="flex items-center gap-2">
                            <Loader size={14} color="white" />
                            <span>{t('resize.inProgress')}</span>
                        </div>
                    ) : (
                        t('resize.button')
                    )}
                </Button>
            </div>

            <ResizeInstanceModal opened={opened} onClose={close} instance={instance} />
        </DialogBox>
    );
}
